<script setup>
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    toRefs,
    watch,
  } from 'vue'

  import Banner from './Banner.vue'
  import CityFilter from './CityFilter.vue'
  import FeaturesFilter from './FeaturesFilter.vue'
  import Loading from '@components/Loading.vue'
  import Pagination from '@components/Pagination.vue'
  import Property from '@components/PropertyLarge.vue'
  import Sort from './Sort.vue'

  import { useI18n } from '@shared/i18n.js'
  import { usePropertiesEnquiry } from '@composables/usePropertiesEnquiry.js'
  import { useStore } from './useStore.js'
  import { useWatchWindowSize } from '@composables/useWatchWindowSize.js'

  import {
    isMobile,
    pageTop,
  } from '@shared/util.js'

  const props = defineProps({
    banners: Object,
    isListView: Boolean,
  })

  const emits = defineEmits(['refresh'])

  const {
    banners,
  } = toRefs(props)

  const current = ref([])
  const loading = ref(false)
  const page = ref(1)

  const store = useStore()
  const { t, vt } = useI18n('properties.index.js')

  const {
    enquiryPath,
    selected,
    handleCheckProperty
  } = usePropertiesEnquiry()

  const {
    windowWidth,
    watchWindowWidth
  } = useWatchWindowSize()

  const {
    per,
    type
  } = window.app.page

  onMounted(() => {
    refresh()
    nextTick(() => $("#main .sumo_select select").SumoSelect())
    watchWindowWidth()
  })

  if (!isMobile()) {
    watch(
      () => store.activeProperty,
      async (val) => {
        if (val && store.activeBy == 'Map') {
          const index = store.properties.findIndex((property) => property.code == val.code)
          const propertyPage = Math.floor(index / per) + 1
          if (page.value != propertyPage) {
            page.value = propertyPage
            await refresh({ scroll: false })
          }
          setTimeout(() => {
            const element = document.querySelector(`.list-view .property-${val.code}`)
            if (element) {
              element.scrollIntoView({ block: 'center', behavior: 'smooth' })
            }
          }, 100)
        }
      }
    )
  }

  const columnsCount = computed(() => {
    if (windowWidth.value > 1500) {
      return 4
    }
    else if (windowWidth.value > 1020) {
      return 3
    }
    else if (windowWidth.value > 576) {
      return 2
    }
    return 1
  })

  const propertiesAndLargeBanners = computed(() => current.value.reduce((result, property, index) => {
    const banner = largeBanner(result.length)
    result.push({ 'type': 'property', property })
    if (banner) {
      result.push({ 'type': 'large-banner', banner })
    }
    return result
  }, []))

  /**
   * 物件情報のclass
   */
  const classProperty = (property) => {
    const result = {}
    result[`property-${property.code}`] = true
    if (store.activeProperty && store.activeBy == 'Map' && store.activeProperty.code == property.code) {
      result['is-focused-in-map'] = true
    }
    return result
  }

  /**
   * マウスアウト
   */
  const handleMouseLeaveProperty = (property) => {
    // resetActiveProperty()
    store.resetActive()
  }

  /**
   * マウスオーバー
   */
  const handleMouseOverProperty = (property) => {
    // setActiveProperty(property.code)
    store.active(property.code, 'List')
  }

  /**
   * ページ移動
   */
  const handleMovePage = (value) => refresh()

  /**
   * ラージバナー取得
   * indexは物件のインデックス
   */
  const largeBanner = (index) => {
    if (!banners.value.large) return

    const i = { 8: 0, 18: 1, 28: 2, 38: 3, 48: 4, 58: 5, 68: 6, 78: 7 }[index]
    if (i == undefined || banners.value.large.length < i) return

    if (banners.value.large[i]) {
      banners.value.large[i].id = `lbnr${i + 1}u`
    }
    return banners.value.large[i]
  }

  /**
   * 物件一覧の表示
   * 表示対象のページの物件情報をajaxで取得する
   */
  const refresh = async (args = {}) => {
    if (args.scroll !== false) {
      pageTop(100)
    }
    loading.value = true
    const target = store.properties.slice(per * (page.value - 1), per * page.value)
    await Promise.allSettled(target.map((property) => store.getProperty(property.code)))
      .then((results) => {
        current.value = results.filter((result) => result.status == 'fulfilled').map((result) => result.value.property)
        const set = new Set(selected.value)
        current.value.forEach((property) => property.selected = set.has(property.code))
        loading.value = false
        emits('refresh')
        if (args.scroll !== false) {
          nextTick(() => pageTop(100))
        }
      })
  }

  /**
   * トップバナー取得
   * PC版：3段目と6段目の後
   * スマホ版：5件目と10件目の後
   *
   * ラージバナーが入っていると物件のインデックスとズレる
   */
  const topBanner = (index) => {
    if (!props.isListView || !banners.value.top || index < 2) return null

    // 計算で出すのが面倒になったのでマッピングを作った、、、
    const map = {
      1: {
        4: 0, 9: 1
      },
      2: {
        5: 0, 11: 1
      },
      3: {
        8: 0, 17: 1
      },
      4: {
        11: 0, 23: 1
      }
    }

    const i = map[columnsCount.value][index]
    if (i == undefined) return null

    return banners.value.top.length > i ? { banner: banners.value.top[i], id: `banner_main${i + 1}u` } : null
  }
</script>

<template>
  <template v-if="store.properties.length > 0">
    <div class="c-sumoselect property_list_sort sumo_select">
      <p>{{ vt('sort') }}</p>
      <Sort />
      <CityFilter v-if="false" :cities="store.cities" />
      <FeaturesFilter />
    </div>
    <div class="listing list-view">
      <template v-if="loading">
        <Loading style="margin-top: 100px; width: 100%;" />
      </template>
      <template v-else>
        <template v-for="(data, index) in propertiesAndLargeBanners">
          <template v-if="data.type == 'property'">
            <Property
              :class="classProperty(data.property)"
              :key="`property-${data.property.code}`"
              :property="data.property"
              @check="handleCheckProperty(data.property, current)"
              @mouseleave="() => handleMouseLeaveProperty(data.property)"
              @mouseover="() => handleMouseOverProperty(data.property)"
            />
          </template>

          <template v-if="topBanner(index)">
            <Banner :banner="topBanner(index).banner" :key="`banner-top-${index}`" :id="topBanner(index).id" class="item c-banner" />
          </template>

          <template v-if="data.type == 'large-banner'">
            <Banner :banner="data.banner" :key="`banner-large-${index}`" :id="data.banner.id" class="item c-ad" />
          </template>
        </template>
      </template>
    </div>

    <Pagination
      v-show="!loading"
      v-model="page"
      :max-links-count="isListView ? 10 : 7"
      :per="per"
      :total="store.properties.length"
      @move="handleMovePage"
    />

    <div :class="{ 'is-active': selected.length > 0 }" class="content_small" id="fixed_contact_links">
      <a :href="enquiryPath" class="btn">{{ t('enquiries.show.js.submit') }}（<span class="num">{{ selected.length }}</span>）</a>
    </div>
  </template>
  <template v-else>
    <div class="listing" v-html="vt('empty')"></div>
  </template>
</template>
